/* eslint-disable camelcase -- GA4 and Snowplow define the wire parameter names. */
export interface Ga4Event {
  name: string;
  params: Record<string, string | number>;
}

export interface CaptureWindow extends Window {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: IArguments[];
  torreGa4Pending?: Record<string, unknown>[];
  torreGa4Capture?: (payload: Record<string, unknown>) => void;
}

const token = (value: unknown): value is string => typeof value === "string" && /^[a-zA-Z0-9_.:-]{1,100}$/.test(value);

export function toGa4Event(payload: Record<string, unknown>): Ga4Event | null {
  if (typeof payload.eid !== "string" || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(payload.eid) ||
      !token(payload.e) || !["string", "number"].includes(typeof payload.dtm) ||
      !Number.isSafeInteger(Number(payload.dtm)) || Number(payload.dtm) <= 0) {
    return null;
  }

  const params: Ga4Event["params"] = {
    source_event_id: payload.eid,
    source_timestamp_ms: Number(payload.dtm),
    source_event_type: payload.e,
    ...(token(payload.aid) ? { app_id: payload.aid } : {})
  };

  if (payload.e === "se") {
    if (token(payload.se_ca)) { params.se_category = payload.se_ca; }
    if (token(payload.se_ac)) { params.se_action = payload.se_ac; }
    if ((typeof payload.se_va === "number" || (typeof payload.se_va === "string" && payload.se_va.trim() !== "")) &&
        Number.isFinite(Number(payload.se_va))) {
      params.se_value = Number(payload.se_va);
    }
  }
  if (payload.e === "ue") {
    try {
      const encoded = typeof payload.ue_px === "string" ? payload.ue_px : null;
      const json = encoded ? atob(encoded.replace(/-/g, "+").replace(/_/g, "/")) : payload.ue_pr;
      const event = (typeof json === "string" ? JSON.parse(json) : json)?.data;
      const match = typeof event?.schema === "string" &&
        /^iglu:([a-zA-Z0-9_.-]+)\/([a-zA-Z0-9_-]+)\/jsonschema\/([0-9]+-[0-9]+-[0-9]+)$/.exec(event.schema);

      if (match) {
        params.source_event_vendor = match[1];
        params.source_event_name = match[2];
        params.source_event_version = match[3];
      }
    } catch {
      // Malformed optional schema metadata must not suppress the original event receipt.
    }
  }
  const name = payload.e === "pv"
    ? "page_view"
    : payload.e === "pp"
      ? "torre_page_ping"
      : payload.e === "se" ? "torre_structured_event" : payload.e === "ue" ? "torre_self_describing" : "torre_tracker_event";

  return { name, params };
}
